import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Download, Package, Wallet, Receipt, TrendingUp } from "lucide-react";
import { supabase as sb } from "@/integrations/supabase/client";

const ESTADO_VARIANT: Record<string, string> = {
  "En Origen": "bg-slate-100 text-slate-700 border-slate-200",
  "En Tránsito": "bg-blue-50 text-blue-700 border-blue-200",
  "En Aduana": "bg-amber-50 text-amber-700 border-amber-200",
  "Nacionalizado": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Entregado": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const fmtCOP = (v: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(v);

interface Props {
  embarqueId: string | null;
  onClose: () => void;
}

export function PortalDossierModal({ embarqueId, onClose }: Props) {
  const open = !!embarqueId;

  const { data, isLoading } = useQuery({
    queryKey: ["portal-dossier", embarqueId],
    enabled: open,
    queryFn: async () => {
      // Embarque (RLS filtra automáticamente vía check_user_is_client_of)
      const { data: emb, error: e1 } = await supabase
        .from("embarques")
        .select("*")
        .eq("id", embarqueId!)
        .maybeSingle();
      if (e1) throw e1;

      // Documentos
      const { data: docs, error: e2 } = await supabase
        .from("registro_documentos")
        .select("id, no_documento, tipo_documento, fecha, total_factura, moneda, archivo_url")
        .eq("embarque_id", embarqueId!)
        .order("created_at", { ascending: false });
      if (e2) throw e2;

      // Inventario (para cálculo de Landed Cost)
      const docIds = (docs ?? []).map((d) => d.id);
      let items: any[] = [];
      if (docIds.length > 0) {
        const { data: inv, error: e3 } = await supabase
          .from("base_inventario")
          .select("sub_total, costo_nacionalizado_unitario_cop, valor_arancel_cop, valor_iva_cop, cantidad")
          .in("documento_id", docIds);
        if (e3) throw e3;
        items = inv ?? [];
      }

      // Gastos logísticos
      const { data: gastos, error: e4 } = await supabase
        .from("gastos_embarque")
        .select("monto, moneda, concepto")
        .eq("embarque_id", embarqueId!);
      if (e4) throw e4;

      // Cálculos
      const trm = Number(emb?.trm_aduana ?? 0);
      const totalMercanciaCOP = (docs ?? []).reduce((acc, d) => {
        const v = Number(d.total_factura ?? 0);
        const rate = (d.moneda ?? "").toUpperCase() === "COP" ? 1 : trm || 1;
        return acc + v * rate;
      }, 0);

      const totalArancel = items.reduce((a, i) => a + Number(i.valor_arancel_cop ?? 0), 0);
      const totalIva = items.reduce((a, i) => a + Number(i.valor_iva_cop ?? 0), 0);
      const totalGastos = (gastos ?? []).reduce((acc, g) => {
        const m = Number(g.monto ?? 0);
        const rate = (g.moneda ?? "").toUpperCase() === "COP" ? 1 : trm || 1;
        return acc + m * rate;
      }, 0);
      const totalLanded = items.reduce(
        (a, i) =>
          a + Number(i.costo_nacionalizado_unitario_cop ?? 0) * Number(i.cantidad ?? 0),
        0,
      );
      const totalGeneral = totalLanded > 0 ? totalLanded : totalMercanciaCOP + totalArancel + totalIva + totalGastos;

      return {
        embarque: emb,
        docs: docs ?? [],
        finanzas: {
          mercancia: totalMercanciaCOP,
          arancel: totalArancel,
          iva: totalIva,
          gastos: totalGastos,
          total: totalGeneral,
        },
      };
    },
  });

  async function handleDownload(archivoUrl: string | null, nombre: string) {
    if (!archivoUrl) return;
    let path = archivoUrl;
    const m = path.match(/\/storage\/v1\/object\/(?:public|sign)\/documentos_kargo\/(.+)$/);
    if (m) path = m[1];
    if (path.startsWith("documentos_kargo/")) path = path.slice("documentos_kargo/".length);
    const { data, error } = await sb.storage
      .from("documentos_kargo")
      .createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) return;
    window.open(data.signedUrl, "_blank");
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0 bg-white">
        <DialogHeader className="px-8 pt-8 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Embarque
              </p>
              <DialogTitle className="text-2xl font-light tracking-tight">
                {data?.embarque?.numero_do ?? "—"}
              </DialogTitle>
            </div>
            {data?.embarque?.estado_logistico && (
              <Badge
                variant="outline"
                className={`${ESTADO_VARIANT[data.embarque.estado_logistico] ?? ""} font-medium px-3 py-1 rounded-full`}
              >
                {data.embarque.estado_logistico}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <div className="px-8 py-6 space-y-8">
            {isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <>
                {/* RESUMEN FINANCIERO */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                    <TrendingUp className="h-3.5 w-3.5" />
                    Resumen Financiero (Landed Cost)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <FinanceCard
                      icon={<Package className="h-4 w-4" />}
                      label="Valor Mercancía"
                      value={fmtCOP(data!.finanzas.mercancia)}
                    />
                    <FinanceCard
                      icon={<Receipt className="h-4 w-4" />}
                      label="Aranceles"
                      value={fmtCOP(data!.finanzas.arancel)}
                    />
                    <FinanceCard
                      icon={<Receipt className="h-4 w-4" />}
                      label="IVA"
                      value={fmtCOP(data!.finanzas.iva)}
                    />
                    <FinanceCard
                      icon={<Wallet className="h-4 w-4" />}
                      label="Gastos Logísticos"
                      value={fmtCOP(data!.finanzas.gastos)}
                    />
                  </div>

                  <div className="mt-4 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 px-6 py-5 flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1">
                        Costo Total Nacionalizado
                      </p>
                      <p className="text-3xl font-light text-white tabular-nums">
                        {fmtCOP(data!.finanzas.total)}
                      </p>
                    </div>
                    {data?.embarque?.liquidado && (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20">
                        Liquidado
                      </Badge>
                    )}
                  </div>
                </section>

                <Separator />

                {/* RUTA */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                    Información del Embarque
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 text-sm">
                    <InfoCell label="Tracking" value={data?.embarque?.tracking_number ?? "—"} />
                    <InfoCell label="Naviera" value={data?.embarque?.shipping_line ?? "—"} />
                    <InfoCell
                      label="ETA"
                      value={data?.embarque?.fecha_llegada_estimada ?? "—"}
                    />
                    <InfoCell
                      label="Llegada Real"
                      value={data?.embarque?.fecha_llegada_real ?? "—"}
                    />
                  </div>
                </section>

                <Separator />

                {/* DOCUMENTOS */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                    Documentos ({data?.docs.length ?? 0})
                  </h3>
                  {data?.docs.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">
                      No hay documentos disponibles.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {data?.docs.map((d) => (
                        <div
                          key={d.id}
                          className="flex items-center justify-between p-4 rounded-xl border bg-slate-50/50 hover:bg-slate-100/60 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 rounded-lg bg-white border flex items-center justify-center shrink-0">
                              <FileText className="h-4 w-4 text-slate-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{d.no_documento}</p>
                              <p className="text-xs text-muted-foreground">
                                {d.tipo_documento ?? "Documento"} · {d.fecha ?? "—"}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-2 shrink-0"
                            onClick={() => handleDownload(d.archivo_url, d.no_documento)}
                            disabled={!d.archivo_url}
                          >
                            <Download className="h-3.5 w-3.5" />
                            Descargar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function FinanceCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        {icon}
        <span className="text-[11px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-base font-medium text-slate-900 tabular-nums">{value}</p>
    </div>
  );
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
        {label}
      </p>
      <p className="text-sm text-slate-900 font-medium">{value}</p>
    </div>
  );
}
